import { LearnMoreButton } from 'components/shared/LearnMore/button';
import { ProductPinnedButton } from 'components/shared/ProductAndClientSelector/ProductPinnedButton';
import { useHelpCenterProducts } from 'hooks/use-help-center-products';
import { useTranslateProductType } from 'hooks/use-translate-payoff';
import { sortBy } from 'lodash-es';
import { ProductHelpCenterAction } from 'models';
import type { ProductVariation } from 'models/productsReferential';
import { FC, useMemo } from 'react';
import { ProductSubtypeName } from 'routes/essential-workspace/components/product-selector/catalog/product-subtype-name';
import { usePinnedProducts } from 'routes/essential-workspace/components/product-selector/hooks/use-pinned';

export type ProductSubTypeSelectorProps = {
  products: readonly ProductVariation[];
  selectedProductType?: string;
  selectedAssetType?: string;
  onSelectProductSubType: (selectedProductType: string, productSubType: string) => void;
};

export const ProductSubTypeSelector: FC<ProductSubTypeSelectorProps> = ({
  products,
  selectedProductType,
  selectedAssetType,
  onSelectProductSubType,
}) => {
  const productSubTypeOptions = useGetProductSubTypeOptions(products, selectedProductType, selectedAssetType);
  const { isProductPinned } = usePinnedProducts('essential');
  const { openLearnMore, availableProducts } = useHelpCenterProducts();

  return (
    <ul
      className={`${selectedAssetType === 'Pinned' ? 'col-9' : 'col-5'} h-100 p-0 overflow-y-auto scrollbar-sm list-unstyled mb-0`}
      aria-label="Product subtypes"
    >
      {productSubTypeOptions.map(({ value, label }) => {
        const productVariation = products.find(p => p.productSubType === value)!;

        const productName = `${selectedProductType}.${value}`;

        const { hasVideo, hasFactSheet } = availableProducts?.[productName] || {};

        const onClickLearnMore = (action: ProductHelpCenterAction) => {
          openLearnMore(selectedProductType, value, action === 'Download factsheet');
          return false;
        };

        return (
          <li key={value}>
            <button
              className="btn btn-flat-primary btn-block btn-lg btn-text-icon border-bottom border-opacity-20 ps-3 pe-2 py-1"
              onClick={() => onSelectProductSubType(productVariation.productType, value)}
            >
              <ProductSubtypeName
                pinned={selectedAssetType === 'Pinned'}
                productType={productVariation.productType}
                productSubType={label}
              />
              <div className="d-flex align-items-center">
                {hasVideo && (
                  <LearnMoreButton
                    labelId="learnMore.video"
                    icon="play_circle_outline"
                    iconOnly
                    variant="flat-secondary"
                    onClick={() => onClickLearnMore('Start video')}
                  />
                )}
                {hasFactSheet && (
                  <LearnMoreButton
                    labelId="learnMore.factsheet"
                    icon="text_snippet"
                    iconOnly
                    variant="flat-secondary"
                    iconClassName="icon-outlined"
                    onClick={() => onClickLearnMore('Download factsheet')}
                  />
                )}
                <ProductPinnedButton
                  productVariation={productVariation}
                  tabType="catalogue"
                  workspace="essential"
                  iconName={isProductPinned(productVariation) ? 'star' : 'star_outline'}
                />
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const useGetProductSubTypeOptions = (
  productVariations: readonly ProductVariation[],
  productType?: string,
  selectedAssetType?: string,
) => {
  const translateProductSubtype = useTranslateProductType();

  const filteredProductSubTypes = productVariations.filter(
    product =>
      product.assetTypes?.some(assetType => selectedAssetType === assetType) &&
      (selectedAssetType === 'Pinned' || product.productType === productType),
  );

  return useMemo(() => {
    if (productType === undefined) {
      return [];
    }

    const distinctProductSubTypeOptions = filteredProductSubTypes
      .filter(
        ({ productSubType }, pos, all) => all.findIndex(product => product.productSubType === productSubType) === pos,
      )
      .map(({ productSubType, commercialProductSubType }) => ({
        label: translateProductSubtype(productSubType, commercialProductSubType),
        value: productSubType,
      }));

    return sortBy(distinctProductSubTypeOptions, o => o.label);
  }, [productType, filteredProductSubTypes, translateProductSubtype]);
};
